import { AmountRateType, ApprovalStatus, Currency, Incoterms, PickupType } from "@constant/ThroughShipment";
import { ListFilter } from "@shared/types";
import downloadFileFromResponse from "@utils/downloadFileFromResponse";

import CoreAPI from "../CoreAPI";
import { transformAPIKeyToCamel } from "../utils/transformAPIKeyToCamel";
import { transformCamelToSnake } from "../utils/transformCamelToSnake";

const apiClient = new CoreAPI();

export interface ExternalOrderLine {
  id: number;
  no: string;
  externalOrder: number;
  batchNumber: null;
  sku: string;
  name: string;
  nameEng: string;
  effectiveDate: null;
  cost: number;
  pcsPerCarton: number;
  orderedQty: number;
  quotedPriceTwd: number;
  quotedPriceCurrency: number;
  quotedPrice: number;
  totalDeduction: number;
  totalPostDeduction: number;
  totalQty: number;
  margin: number;
  revenue: number;
  canAcceptDays: null;
  shippedQty: number;
}

export interface MatoItem {
  matoDirPrefix: string;
  matoFilePath: string;
  id?: number;
  path?: string;
  name?: string;
}

export interface ExternalOrderInfo {
  id: number;
  staff: {
    id: number;
    name: string;
  };
  media: string | null;
  orderNumber: string;
  status: string;
  retailerRegion: string;
  retailerRegionType: string;
  retailerName: string;
  retailerContactName: string;
  retailerContactPhone: string;
  retailerContactEmail: string;
  retailerProductlineName: number[];
  retailerWarehouseName: string;
  retailerContractType: string[];
  retailerDaysAfterMonthlyStatement: number;
  pickupType: PickupType;
  approvalStatus: string;
  sectionManagerApproval: ApprovalStatus;
  managerApproval: ApprovalStatus;
  financeApproval: ApprovalStatus;
  ceoApproval: ApprovalStatus;
  estimatedShippingDate: string;
  specifiedDeliveryDate: string;
  trackingNumber: string;
  shippingMethod: string;
  shippingAt: string;
  currencyRate: number;
  currency: Currency;
  incoterms: Incoterms;
  matoL?: MatoItem[];
  createdAt: string;
  updatedAt: string;
  totalRevenue: number;
  totalCost: number;
  totalOrderFee: number;
  totalMargin: number;
  totalMarginRatio: number;
  expectedRevenue: number;
  expectedCost: number;
  expectedOrderFee: number;
  expectedMargin: number;
  expectedMarginRatio: number;
  externalOrderLine: ExternalOrderLine[];
  orderDeduction: {
    id: number;
    productlineName: string;
    name: string;
    rate: number;
    amount: number;
    externalOrder: number;
    type: AmountRateType;
  }[];
  orderFee: {
    id: number;
    name: string;
    rate: number;
    amount: number;
    externalOrder: number;
  }[];
  isSend: boolean;
  deliveryAt: string;
  totalShippedQty: number;
  totalOrderedQty: number;
  invoiceInfo: InvoiceInfo;
  canInvoice: boolean;
  tryallPurchaseId?: number;
  warehouseCode: string;
  warehouseName: string;
  canDestroy: boolean;
  canCancel: boolean;
}
export type InvoiceInfo = {
  invoiceNumber: string;
  invoiceDate: string;
  invoiceAmount: number;
};
export interface CreateExternalOrderPayload {
  estimatedShippingDate: string;
  retailerRegion: string;
  retailerRegionType: string;
  retailerName: string;
  retailerContactName: string;
  retailerContactPhone: string;
  retailerContactEmail: string;
  retailerProductlineName: string[];
  retailerWarehouseName: string;
  retailerWarehouseAddress: string;
  retailerWarehouseCode: string;
  pickupType: PickupType;
  currencyRate: number;
  currency: Currency;
  incoterms: string;
  matoL?: MatoItem[];
  warehouseCode: string;
  retailerId: number;
  specifiedDeliveryDate: string;
}
export interface CreateExternalOrder {
  id: number;
  staff: {
    id: number;
    name: string;
  };
  media: string | null;
  orderNumber: string;
  status: string;
  retailerRegion: string;
  retailerRegionType: string;
  retailerName: string;
  retailerContactName: string;
  retailerContactPhone: string;
  retailerContactEmail: string;
  retailerProductlineName: string[];
  retailerWarehouseName: string;
  retailerContractType: string[];
  retailerDaysAfterMonthlyStatement: number;
  pickupType: PickupType;
  approvalStatus: string;
  sectionManagerApproval: ApprovalStatus;
  managerApproval: ApprovalStatus;
  financeApproval: ApprovalStatus;
  ceoApproval: ApprovalStatus;
  estimatedShippingDate: string;
  shippingAt: string;
  currencyRate: number;
  currency: Currency;
  incoterms: Incoterms;
  createdAt: string;
  updatedAt: string;
}

export type UpdateExternalOrderPayload = {
  externalOrderId: number;
} & Partial<CreateExternalOrderPayload>;

export interface ApprovalExternalOrder {
  isApproved: boolean;
}
export interface BatchUpdateOrderLines {
  lines: OrderLine[];
  revenues: Revenues[] | [];
}

export interface Revenues {
  sku?: string;
  requestQty?: number;
}
export interface OrderLine {
  id: number;
  batchNumber: string;
  effectiveDate: string | null;
  canAcceptDays?: number;
  cost: number;
  quotedPriceCurrency: number;
  quotedPriceTwd: number;
  pcsPerCarton: number;
  orderedQty: number;
}

export interface OrderLinesParams {
  externalOrder: number | string;
  sku?: string;
  nameQ?: string;
  nameEngQ?: string;
  effectiveDateBefore?: string;
  effectiveDateAfter?: string;
  limit?: number;
}
export interface CreateOrderFeePayload {
  externalOrder: number;
  name: string;
  rate?: number;
  amount?: number;
}
export interface UpdateOrderFeePayload {
  id: number;
  rate?: number;
  amount?: number;
}
export interface DeleteOrderFeePayload {
  orderFeeIds: number[];
}
export interface OrderLinesList {
  id: number;
  no: string;
  externalOrder: number;
  batchNumber: string | null;
  sku: string;
  name: string;
  nameEng: string;
  effectiveDate: string | null;
  effectiveDateBatch: string | null;
  cost: number;
  pcsPerCarton: number;
  orderedQty: number;
  quotedPrice: number;
  quotedPriceTwd: number;
  quotedPriceCurrency: number;
  totalDeduction: number;
  totalQty: number;
  margin: number;
  revenue: number;
  canAcceptDays: number | null;
  canSalesQty: number;
  storageType: string;
  notes: string;
  showData: boolean;
  totalStock: number;
  validErrorMessage: string;
}
export interface OrderLinesListResult {
  count: number;
  next: string | null;
  previous: string | null;
  results: OrderLinesList[];
}
export interface OrderDeductionsList {
  id: number;
  productlineName: string;
  name: string;
  rate?: number;
  amount?: number;
  externalOrder: number;
  type: AmountRateType;
}
export interface OrderDeductionsListResult {
  count: number;
  next: string | null;
  previous: string | null;
  results: OrderDeductionsList[];
}
export interface OrderFeeList {
  id: number;
  name: string;
  rate?: number;
  amount?: number;
  externalOrder: number;
  type: AmountRateType;
}
export interface OrderFeeListResult {
  count: number;
  next: string | null;
  previous: string | null;
  results: OrderFeeList[];
}
export interface ExternalOrdersListResult {
  count: number;
  next: string | null;
  previous: string | null;
  results: ExternalOrdersList[];
}

export interface ExternalOrdersList {
  id: number;
  staff: {
    id: number;
    name: string;
  };
  orderNumber: string;
  status: string;
  warehouseCode: string;
  retailerRegion: string;
  retailerRegionType: string;
  retailerName: string;
  retailerContactName: string;
  retailerContactPhone: string;
  retailerContactEmail: string;
  retailerProductlineName: string[];
  retailerWarehouseName: string;
  retailerContractType: string[];
  retailerDaysAfterMonthlyStatement: number;
  pickupType: PickupType;
  approvalStatus: string;
  sectionManagerApproval: number;
  managerApproval: number;
  financeApproval: number;
  ceoApproval: number;
  estimatedShippingDate: string;
  shippingAt: string;
  currencyRate: number;
  currency: number;
  incoterms: string;
  createdAt: string;
  updatedAt: string;
  media: null;
  canInvoice: boolean;
  deliveryAt: string;
}

export interface ExternalOrdersExportParam {
  orderNumber?: string;
  retailerNameQ?: string;
  status?: number;
  staff?: number;
  approvalStatus?: number;
  createdAtAfter?: string;
  createdAtBefore?: string;
  shippingAtAfter?: string;
  shippingAtBefore?: string;
}

export interface ExternalOrdersFilterParam extends ListFilter, ExternalOrdersExportParam {}
export interface ExternalOrdersRevenueList {
  count: number;
  next: string | null;
  previous: string | null;
  results: ExternalOrdersRevenue[];
}
export interface ExternalOrdersRevenue {
  sku: string;
  name: string;
  totalRevenue: number;
  externalOrderId: number;
  requestQty: number;
}

interface ExternalOrderApi {
  fetchExternalOrdersList: (payload: ExternalOrdersFilterParam) => Promise<ExternalOrdersListResult>;
  fetchExternalOrderInfo: (externalOrderId: number) => Promise<ExternalOrderInfo>;
  fetchCreateExternalOrder: (payload: CreateExternalOrderPayload) => Promise<CreateExternalOrder>;
  fetchUpdateExternalOrder: (externalOrderId: number, payload: Partial<CreateExternalOrderPayload>) => Promise<void>;
  fetchDeleteExternalOrder: (externalOrderId: number) => Promise<void>;
  fetchCancelExternalOrder: (externalOrderId: number) => Promise<void>;
  fetchApprovalExternalOrder: (externalOrderId: number, payload: ApprovalExternalOrder) => Promise<void>;
  fetchOrderLines: (params: OrderLinesParams) => Promise<OrderLinesListResult>;
  fetchCreateProduct: (params: { externalOrder: number; no: string }) => Promise<OrderLinesListResult>;
  fetchDeleteProduct: (params: { externalOrder: number; id: number }) => Promise<void>;
  fetchBatchUpdateOrderLines: (payload: { externalOrder: number; orderLines: BatchUpdateOrderLines }) => Promise<void>;
  fetchOrderDeductions: (externalOrderId: number) => Promise<OrderDeductionsListResult>;
  fetchOrderFeeList: (externalOrderId: number) => Promise<OrderFeeListResult>;
  fetchBatchCreateOrderFee: (payload: CreateOrderFeePayload[]) => Promise<void>;
  fetchBatchUpdateOrderFee: (payload: UpdateOrderFeePayload[]) => Promise<void>;
  fetchBatchDeleteOrderFee: (payload: DeleteOrderFeePayload) => Promise<void>;
  fetchSendOrderNotify: (externalOrderId: number) => Promise<void>;
  importIWappsProductsXLS: (params: { externalOrder: number; file: File }) => Promise<void>;
  exportCSV: (filter: ExternalOrdersExportParam) => Promise<void>;
  fetchCreateInvoice: (externalOrderId: number, payload: InvoiceInfo) => Promise<void>;
  exportExternalProductRevenue: (externalOrderId: number) => Promise<void>;
  importExternalProductRevenue: (externalOrderId: number, file: File) => Promise<void>;
  fetchExternalOrdersRevenue: (externalOrderId: number) => Promise<ExternalOrdersRevenueList>;
}

const externalOrderApi: ExternalOrderApi = {
  fetchExternalOrdersList: async (payload) => {
    const response = await apiClient.getData("/manage/retail/external-orders/", transformCamelToSnake(payload));
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchExternalOrderInfo: async (externalOrderId) => {
    const response = await apiClient.getData(`/manage/retail/external-orders/${externalOrderId}/`, {});
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchCreateExternalOrder: async (payload) => {
    const response = await apiClient.postData("/manage/retail/external-orders/", transformCamelToSnake(payload));
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchUpdateExternalOrder: async (externalOrderId, payload) => {
    await apiClient.patchData(`/manage/retail/external-orders/${externalOrderId}/`, transformCamelToSnake(payload));
  },
  fetchDeleteExternalOrder: async (externalOrderId) => {
    await apiClient.deleteData(`/manage/retail/external-orders/${externalOrderId}/`, {});
  },
  fetchCancelExternalOrder: async (externalOrderId) => {
    await apiClient.putData(`/manage/retail/external-orders/${externalOrderId}/cancel/`, {});
  },
  fetchApprovalExternalOrder: async (externalOrderId, payload) => {
    await apiClient.putData(
      `/manage/retail/external-orders/${externalOrderId}/approve/`,
      transformCamelToSnake(payload),
    );
  },
  fetchOrderLines: async (params) => {
    const { externalOrder, ...restParams } = params;
    const response = await apiClient.getData(
      `/manage/retail/external-orders/${externalOrder}/order-lines/`,
      transformCamelToSnake(restParams),
    );
    return transformAPIKeyToCamel(response.data.result);
  },
  importIWappsProductsXLS: async (params) => {
    const formData = new FormData();
    formData.append("file", params.file);
    const response = await apiClient.postData(
      `/manage/retail/external-orders/${params.externalOrder}/order-lines/upload/`,
      formData,
    );
    return response.data;
  },
  fetchCreateProduct: async (params) => {
    const { externalOrder, ...restParams } = params;
    const response = await apiClient.postData(
      `/manage/retail/external-orders/${externalOrder}/order-lines/`,
      transformCamelToSnake(restParams),
    );
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchDeleteProduct: async (params) => {
    await apiClient.deleteData(`/manage/retail/external-orders/${params.externalOrder}/order-lines/${params.id}/`, {});
  },
  fetchBatchUpdateOrderLines: async (payload) => {
    const { externalOrder, orderLines } = payload;
    await apiClient.putData(
      `/manage/retail/external-orders/${externalOrder}/order-lines/batch-update/`,
      transformCamelToSnake(orderLines),
    );
  },
  fetchOrderDeductions: async (externalOrderId) => {
    const response = await apiClient.getData(`/manage/retail/order-deductions/?external_order=${externalOrderId}`, {});
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchOrderFeeList: async (externalOrderId) => {
    const response = await apiClient.getData(`/manage/retail/order-fee/?external_order=${externalOrderId}`, {});
    return transformAPIKeyToCamel(response.data.result);
  },
  fetchBatchCreateOrderFee: async (payload) => {
    await apiClient.postData("/manage/retail/order-fee/batch-create/", transformCamelToSnake(payload));
  },
  fetchBatchUpdateOrderFee: async (payload) => {
    await apiClient.putData("/manage/retail/order-fee/batch-update/", transformCamelToSnake(payload));
  },
  fetchBatchDeleteOrderFee: async (payload) => {
    await apiClient.putData("/manage/retail/order-fee/batch-delete/", transformCamelToSnake(payload));
  },
  fetchSendOrderNotify: async (externalOrderId) => {
    await apiClient.postData(`/manage/retail/external-orders/${externalOrderId}/send-notify/`, {});
  },
  exportCSV: async (filter) => {
    const getParams = transformCamelToSnake(filter);
    await apiClient.getData("/manage/retail/external-orders/export/", getParams);
  },
  fetchCreateInvoice: async (externalOrderId, payload) => {
    await apiClient.postData(
      `/manage/retail/external-orders/${externalOrderId}/invoice/`,
      transformCamelToSnake(payload),
    );
  },
  exportExternalProductRevenue: async (externalOrderId) => {
    const response = await apiClient.getData(
      `/manage/retail/external-orders/${externalOrderId}/revenue/export/`,
      {},
      { responseType: "blob" },
    );
    downloadFileFromResponse(response);
  },

  importExternalProductRevenue: async (externalOrderId: number, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await apiClient.putData(
      `/manage/retail/external-orders/${externalOrderId}/revenue/upload/`,
      formData,
    );

    return transformAPIKeyToCamel(response.data);
  },
  fetchExternalOrdersRevenue: async (externalOrderId: number) => {
    const response = await apiClient.getData(`/manage/retail/external-orders/${externalOrderId}/revenue/`, {});

    return transformAPIKeyToCamel(response.data.result);
  },
};

export default externalOrderApi;
